import React from 'react';
import {useSelector} from 'react-redux';
import {selectCaseById} from '../features/casesSlice';
import {selectToolboxById} from '../features/toolboxSlice';
import {selectCommentById} from '../features/commentsSlice';
import {formatDate, formatName, prepareText} from '../util/format';
import {selectUserById} from '../features/usersSlice';
import {selectToolById} from '../features/toolsSlice';
import {selectTimezone} from '../features/timezoneSlice';
import {ScrollArea} from './ui/scroll-area';
import {UserAvatar} from '@/components/UserAvatar';
import {cn} from '@/lib/utils';

export const CaseCommentsList = ({caseId, blurComments}: {
    caseId: string;
    blurComments?: boolean;
}) => {
    const currentCase = useSelector(state => selectCaseById(state, caseId));

    if (!currentCase) {
        return null;
    }

    return <CommentsListContainer comments={currentCase.comments} blurComments={blurComments} />
}


export const ToolboxCommentsList = ({toolboxId, toolId}: {
    toolboxId: string;
    toolId?: string;
}) => {
    const currentToolbox = useSelector(state => selectToolboxById(state, toolboxId));
    const currentTool = useSelector(state => toolId ? selectToolById(state, toolId) : null);

    if (toolId && !currentTool) {
        return null;
    }

    if (!currentToolbox) {
        return null;
    }

    const comments = (toolId ? (currentTool?.comments || []) : currentToolbox.comments) || [];

    return <CommentsListContainer comments={comments} />
}

const CommentsListContainer = ({comments, blurComments}: {
    comments: string[];
    blurComments?: boolean;
}) => {
    return (
        <ScrollArea className={cn("h-full w-full")} style={{
            filter: blurComments ? 'blur(4px) grayscale(1)' : undefined,
        }}>
            <div className="grid gap-6">
                {(comments || []).map((id) => {
                    return <Comment key={id} commentId={id} />
                })}
            </div>
        </ScrollArea>
    )
}

type CommentProps = {
    commentId: string;
}

const Comment = ({commentId}: CommentProps) => {
    const comment = useSelector(state => selectCommentById(state, commentId));
    const timezone = useSelector(selectTimezone);
    const commentAuthor = useSelector(state => {
        return selectUserById(state, comment.user);
    });
    if (!comment || !comment.content || comment?.content?.length < 3) {
        return null;
    }

    return (
        <div className="flex items-center justify-between space-x-4">
            <div className="flex items-start space-x-4">
                <UserAvatar userId={comment.user} />
                <div>
                    <p className="text-sm font-medium leading-none">{formatName(commentAuthor)}</p>
                    <p className="text-sm text-muted-foreground">{formatDate({ts: comment.created, tz: timezone})}</p>
                    {
                        prepareText(comment.content || '').map((line: string, index: number) => (
                            <p className="text-sm" key={index}>{line}</p>
                        ))
                    }
                </div>
            </div>
        </div>
    )
}

